import {skipToken} from '@reduxjs/toolkit/query';
import {
  Button,
  ButtonGroup,
  Card,
  closeCurrentDialog,
  DataStatus,
  Form,
  FormButton,
  FormField,
  FormSubmitHandler,
  showNotification,
} from 'platform/components';
import {Grid, GridItem, Hide, VStack} from 'platform/foundation';

import {isNil, isNotNil} from 'ramda';

import {
  RecordAuditSeriesDefinitionResponseBody,
  useCreateRecordAuditSeriesDefinitionMutation,
  useGetBranchListQuery,
  useGetRecordAuditSeriesDefinitionListQuery,
  useGetRecordAuditSeriesDefinitionQuery,
  useGetSeriesListQuery,
  useUpdateRecordAuditSeriesDefinitionMutation,
} from '@dms/api/core';
import i18n from '@dms/i18n';
import {getOptionsFromSeries} from '@dms/settings/shared';
import {
  getRecordAuditPairResourceLabel,
  handleApiError,
  RECORD_AUDIT_PAIR_RESOURCE_IDS,
  RecordAuditPairResourceId,
} from '@dms/shared';

import {suffixTestId, TestIdProps} from 'shared/web';

import {RECORD_AUDIT_SERIES_TYPE} from '../constants/RECORD_AUDIT_SERIES_TYPE';
import {
  getRecordAuditSeriesDefinitionFormSchema,
  RecordAuditSeriesDefinitionFormValues,
} from '../utils/getRecordAuditSeriesDefinitionFormSchema';

interface RecordAuditSeriesDefinitionFormProps extends TestIdProps {
  definitionId?: RecordAuditSeriesDefinitionResponseBody['id'];
}

export function RecordAuditSeriesDefinitionForm(props: RecordAuditSeriesDefinitionFormProps) {
  const {data: branches} = useGetBranchListQuery();

  const {data: series} = useGetSeriesListQuery({
    type: [RECORD_AUDIT_SERIES_TYPE],
  });

  const {data: definitions = []} = useGetRecordAuditSeriesDefinitionListQuery();

  const {
    data: definition,
    isFetching: isFetchingDefinition,
    isError: isErrorDefinition,
  } = useGetRecordAuditSeriesDefinitionQuery(
    isNotNil(props.definitionId) ? {seriesDefinitionId: props.definitionId} : skipToken
  );

  const [updateSeriesDefinition, {isLoading: isLoadingUpdate}] =
    useUpdateRecordAuditSeriesDefinitionMutation();

  const [createSeriesDefinition, {isLoading: isLoadingCreate}] =
    useCreateRecordAuditSeriesDefinitionMutation();

  // Audit numbers are unique tenant-wide, so the BE refuses non-unique series (DMS-AUD-SD-0008).
  const recordAuditSeries = getOptionsFromSeries(
    series?.filter((item) => item.unique),
    RECORD_AUDIT_SERIES_TYPE
  );

  const isSystemDefinition = definition?.isSystem === true;
  const schema = getRecordAuditSeriesDefinitionFormSchema(isSystemDefinition);

  // One definition per branch (DMS-AUD-SD-0003): offer only branches no other definition uses.
  const usedBranchIds = definitions
    .filter((item) => item.id !== props.definitionId)
    .map((item) => item.branchId);

  const branchListOptions =
    branches?.branchListItems
      .filter((branch) => !usedBranchIds.includes(branch.id))
      .map((branch) => ({
        value: branch.id,
        label: branch.marketingName,
      })) ?? [];

  const getRecordSeriesId = (resourceId: RecordAuditPairResourceId) =>
    definition?.recordSeries.find((item) => item.resourceId === resourceId)?.seriesId ?? null;

  const handleSuccess = () => {
    showNotification.success(i18n.t('general.notifications.changesSuccessfullySaved'));
    closeCurrentDialog();
  };

  const handleSubmit: FormSubmitHandler<RecordAuditSeriesDefinitionFormValues> = (values) => {
    // Empty record-type pickers are left out — the BE falls back to the default series for them.
    const recordSeries = RECORD_AUDIT_PAIR_RESOURCE_IDS.flatMap((resourceId) => {
      const seriesId = values.recordSeries[resourceId];
      return isNotNil(seriesId) ? [{resourceId, seriesId}] : [];
    });

    if (isNotNil(definition)) {
      return updateSeriesDefinition({
        seriesDefinitionId: definition.id,
        body: {
          name: values.name,
          branchId: values.branchId,
          defaultSeriesId: values.defaultSeriesId,
          recordSeries,
        },
      })
        .unwrap()
        .then(handleSuccess)
        .catch(handleApiError);
    }

    // The schema already requires a branch outside the system definition; this narrows the type.
    if (isNil(values.branchId)) {
      return Promise.resolve();
    }

    return createSeriesDefinition({
      body: {
        name: values.name,
        branchId: values.branchId,
        defaultSeriesId: values.defaultSeriesId,
        recordSeries,
      },
    })
      .unwrap()
      .then(handleSuccess)
      .catch(handleApiError);
  };

  return (
    <DataStatus isLoading={isFetchingDefinition} isError={isErrorDefinition} spacing={5}>
      <Form<RecordAuditSeriesDefinitionFormValues>
        onSubmit={handleSubmit}
        experimentalZodSchema={schema}
        defaultValues={{
          name: definition?.name ?? '',
          branchId: definition?.branchId ?? null,
          defaultSeriesId: definition?.defaultSeriesId ?? '',
          recordSeries: {
            BUSINESS_CASE: getRecordSeriesId('BUSINESS_CASE'),
            SERVICE_CASE: getRecordSeriesId('SERVICE_CASE'),
            SERVICE_ORDER: getRecordSeriesId('SERVICE_ORDER'),
            RENTAL_ORDER: getRecordSeriesId('RENTAL_ORDER'),
            VEHICLE: getRecordSeriesId('VEHICLE'),
            INTEREST: getRecordSeriesId('INTEREST'),
            WORKSHOP_INTEREST: getRecordSeriesId('WORKSHOP_INTEREST'),
          },
        }}
      >
        {(control) => (
          <VStack spacing={4}>
            <Card title={i18n.t('entity.settings.labels.general')} variant="inlineGrey">
              <Grid columns={2}>
                <FormField
                  control={control}
                  name="name"
                  type="text"
                  label={i18n.t('entity.seriesDefinition.labels.definitionName')}
                  data-testid={suffixTestId('definitionName', props)}
                  isRequired
                />
                <GridItem>
                  <Hide when={definition?.isSystem}>
                    <FormField
                      control={control}
                      type="choice"
                      name="branchId"
                      label={i18n.t('entity.branch.labels.branch')}
                      data-testid={suffixTestId('branchId', props)}
                      options={branchListOptions}
                      menuInPortal
                      isRequired
                    />
                  </Hide>
                </GridItem>
                <FormField
                  control={control}
                  type="choice"
                  name="defaultSeriesId"
                  label={i18n.t('page.settings.recordAuditSeriesDefinition.labels.defaultSeries')}
                  data-testid={suffixTestId('defaultSeriesId', props)}
                  options={recordAuditSeries}
                  menuInPortal
                  isRequired
                />
              </Grid>
            </Card>

            <Card
              title={i18n.t('page.settings.recordAuditSeriesDefinition.labels.recordSeries')}
              variant="inlineGrey"
            >
              <Grid columns={2}>
                {RECORD_AUDIT_PAIR_RESOURCE_IDS.map((resourceId) => (
                  <FormField
                    key={resourceId}
                    control={control}
                    type="choice"
                    name={`recordSeries.${resourceId}`}
                    label={getRecordAuditPairResourceLabel(resourceId)}
                    placeholder={i18n.t(
                      'page.settings.recordAuditSeriesDefinition.labels.defaultSeries'
                    )}
                    data-testid={suffixTestId(`recordSeries-${resourceId}`, props)}
                    options={recordAuditSeries}
                    menuInPortal
                  />
                ))}
              </Grid>
            </Card>

            <ButtonGroup align="right">
              <Button
                title={i18n.t('general.actions.cancel')}
                variant="secondary"
                data-testid={suffixTestId('cancel', props)}
                onClick={closeCurrentDialog}
              />
              <FormButton
                control={control}
                isLoading={isLoadingUpdate || isLoadingCreate}
                type="submit"
                data-testid={suffixTestId('submit', props)}
                title={i18n.t(
                  isNotNil(props.definitionId) ? 'general.actions.save' : 'general.actions.create'
                )}
              />
            </ButtonGroup>
          </VStack>
        )}
      </Form>
    </DataStatus>
  );
}
